import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { InstituteLogo } from "@/components/InstituteLogo";
import { INSTITUTE_NAME } from "@/lib/branding";
import { fetchCourse } from "@/lib/api";
import type { CourseResponse, SectionResponse } from "@/types/api";

export function CourseView() {
  const { courseId } = useParams<{ courseId: string }>();
  const [course, setCourse] = useState<CourseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = courseId ? parseInt(courseId, 10) : NaN;
    if (!id || Number.isNaN(id)) {
      setError("Invalid course ID");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetchCourse(id)
      .then(setCourse)
      .catch((e) => setError(e.message || "Failed to load course"))
      .finally(() => setLoading(false));
  }, [courseId]);

  if (loading) {
    return (
      <div className="min-h-full flex flex-col items-center justify-center gap-6 p-10 lp-theme-adult bg-gradient-to-b from-slate-50 to-indigo-50/30">
        <div className="rounded-2xl bg-white p-4 shadow-lg shadow-indigo-100/50 ring-1 ring-slate-100">
          <InstituteLogo height={56} />
        </div>
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <span className="inline-block h-4 w-4 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" aria-hidden />
          Loading course…
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-full flex items-center justify-center p-8 lp-theme-adult bg-slate-50">
        <div className="text-center max-w-md rounded-2xl bg-white p-8 shadow-xl shadow-slate-200/80 ring-1 ring-slate-100">
          <p className="text-red-600 mb-4 font-medium">{error || "Course not found"}</p>
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 transition-colors"
          >
            Back
          </Link>
        </div>
      </div>
    );
  }

  const audience = audienceBadge(course.eal_level);

  return (
    <div className="min-h-full lp-theme-adult relative overflow-hidden">
      <div
        className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50/70"
        aria-hidden
      />
      <div
        className="absolute inset-0 opacity-50 bg-[radial-gradient(ellipse_80%_50%_at_100%_-10%,rgba(99,102,241,0.18),transparent)]"
        aria-hidden
      />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <nav className="mb-8 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors"
          >
            <span aria-hidden className="text-lg leading-none">
              ←
            </span>
            Home
          </Link>
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider hidden sm:block">
            {INSTITUTE_NAME}
          </span>
        </nav>

        <header className="flex flex-col sm:flex-row sm:items-start gap-6 sm:gap-8 mb-10">
          <div className="shrink-0 w-28 h-28 sm:w-32 sm:h-32 rounded-2xl bg-white flex items-center justify-center shadow-xl shadow-indigo-100/60 ring-1 ring-slate-100/80 p-3">
            <InstituteLogo height={112} className="max-h-full" />
          </div>
          <div className="min-w-0 flex-1">
            <h1
              className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-3"
              style={{ fontFamily: "var(--lp-font-heading)" }}
            >
              {course.title}
            </h1>
            {audience && (
              <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-800 text-xs font-semibold px-3 py-1 ring-1 ring-indigo-100">
                {audience}
              </span>
            )}
            {course.description && (
              <p className="mt-4 text-slate-600 text-base leading-relaxed max-w-2xl">
                {course.description}
              </p>
            )}
          </div>
        </header>

        <div className="space-y-10">
          {course.sections.map((section) => (
            <SectionBlock
              key={section.id}
              section={section}
              courseId={parseInt(courseId!, 10)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionBlock({ section, courseId }: { section: SectionResponse; courseId: number }) {
  const interactiveLessons = section.lessons.filter(
    (l) => l.lesson_type === "interactive_slides"
  );

  return (
    <section className="rounded-2xl bg-white/90 backdrop-blur-sm border border-slate-200/80 shadow-lg shadow-slate-200/40 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white">
        <h2
          className="text-lg font-semibold text-slate-900"
          style={{ fontFamily: "var(--lp-font-heading)" }}
        >
          {section.title}
        </h2>
      </div>
      <ul className="divide-y divide-slate-100">
        {interactiveLessons.map((lesson) => (
          <li key={lesson.id}>
            <Link
              to={`/course/${courseId}/lesson/${lesson.id}`}
              className="group flex items-center gap-3 px-5 py-4 hover:bg-indigo-50/50 transition-colors text-left w-full"
            >
              <span className="font-medium text-slate-800 group-hover:text-indigo-900 flex-1 min-w-0">
                {lesson.lesson_title}
              </span>
              {lesson.duration && (
                <span className="text-xs font-medium text-slate-400 shrink-0 tabular-nums">
                  {lesson.duration}
                </span>
              )}
              <span
                className="text-indigo-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-transform shrink-0 text-lg"
                aria-hidden
              >
                →
              </span>
            </Link>
          </li>
        ))}
        {interactiveLessons.length === 0 && (
          <li className="px-5 py-6 text-slate-500 text-sm text-center">No interactive lessons in this section.</li>
        )}
      </ul>
    </section>
  );
}

/** Stage / year band only (no age ranges). */
function audienceBadge(level?: number): string | null {
  if (level == null) return null;
  if (level <= 1) return "Year 1–2";
  if (level <= 3) return "Year 3–6";
  if (level <= 5) return "KS3";
  return "KS4 GCSE";
}
